/**
 * Excel Aktar frontend doğrulayıcıları (DEV-2784, Faz 3).
 *
 * Backend'deki App\Helpers\Aktar\{GuvenliTarih, SayiNormalize, TcVergiDogrulayici}
 * kurallarının TS aynasıdır — iki taraf aynı kabul/ret kararını vermeli. Kullanıcının
 * Excel'de girdiği değeri tespit + normalize etmek için kullanılır (hatalı hücreyi
 * işaretle, anlamlı mesaj ver). Hiçbiri exception atmaz; geçersizde null/false döner.
 */

// --- Para / sayı (backend SayiNormalize::paraToFloat aynası) ---

/**
 * Para/sayı değerini number'a çevirir; çözemezse null.
 * TR ("1.234,56") ve programatik ("1234.56") biçimleri destekler.
 */
export function parFloat(deger: any): number | null {
  if (deger === null || deger === undefined) return null;
  if (typeof deger === 'number') return isNaN(deger) ? null : deger;
  if (typeof deger !== 'string') return null;

  let s = deger.trim();
  if (s === '') return null;
  // Sadece rakam, ayraç ve baştaki '-' olmalı; aksi halde belirsiz (ör. "TL").
  if (!/^-?[\d.,]+$/.test(s)) return null;

  const negatif = s.startsWith('-');
  s = s.replace(/^-/, '');

  const sonNokta = s.lastIndexOf('.');
  const sonVirgul = s.lastIndexOf(',');

  if (sonNokta !== -1 && sonVirgul !== -1) {
    if (sonVirgul > sonNokta) {
      // TR: nokta binlik, virgül ondalık
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      // EN: virgül binlik, nokta ondalık
      s = s.replace(/,/g, '');
    }
  } else if (sonVirgul !== -1) {
    // Sadece virgül → ondalık
    s = s.replace(/,/g, '.');
  }
  // Sadece nokta (veya ayraçsız) → zaten geçerli biçim.
  // Kalan belirsizlikleri (ör. çoklu nokta "1.2.3") Number() NaN yapar → null.
  const sonuc = Number(s);
  if (isNaN(sonuc)) return null;
  return negatif ? -sonuc : sonuc;
}

// --- TC / vergi (backend TcVergiDogrulayici aynası) ---

/** Rakam dışını atar; rakam kalmazsa null. */
export function tcVergiTemizle(deger: any): string | null {
  if (deger === null || deger === undefined || typeof deger === 'object') return null;
  const rakamlar = String(deger).replace(/\D/g, '');
  return rakamlar === '' ? null : rakamlar;
}

/** TC kimlik no: 11 hane, ilk hane 0 değil, resmi kontrol algoritması. */
export function tcGecerliMi(deger: any): boolean {
  const tc = tcVergiTemizle(deger);
  if (tc === null || tc.length !== 11 || tc[0] === '0') return false;

  const d = tc.split('').map((c) => parseInt(c, 10));
  const tekToplam = d[0] + d[2] + d[4] + d[6] + d[8];
  const ciftToplam = d[1] + d[3] + d[5] + d[7];
  let onuncu = ((tekToplam * 7) - ciftToplam) % 10;
  if (onuncu < 0) onuncu += 10;
  if (onuncu !== d[9]) return false;

  const ilkOnToplam = d.slice(0, 10).reduce((a, b) => a + b, 0);
  return (ilkOnToplam % 10) === d[10];
}

/** Vergi no: 10 haneli sayısal (biçim kontrolü). */
export function vergiGecerliMi(deger: any): boolean {
  const v = tcVergiTemizle(deger);
  return v !== null && v.length === 10;
}

// --- Tarih (backend GuvenliTarih aynası + Excel Date objesi desteği) ---

function gecerliTakvimGunu(gun: number, ay: number, yil: number): boolean {
  if (ay < 1 || ay > 12 || gun < 1 || gun > 31) return false;
  const dt = new Date(Date.UTC(yil, ay - 1, gun));
  return dt.getUTCFullYear() === yil && dt.getUTCMonth() === ay - 1 && dt.getUTCDate() === gun;
}

function isoYap(gun: number, ay: number, yil: number): string {
  const yy = String(yil).padStart(4, '0');
  const mm = String(ay).padStart(2, '0');
  const dd = String(gun).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/**
 * Tarihi ISO (YYYY-MM-DD) biçimine çevirir; çözemezse null.
 * Date objesi (Excel cellDates), GG/AA/YYYY (./-, tek/çift hane), saat ekli ve
 * hazır ISO desteklenir. Saf sayı (Excel serial) tarih sayılmaz — yanlış çevrimi önler.
 */
export function parseTarih(deger: any): string | null {
  if (deger === null || deger === undefined || deger === '') return null;

  if (deger instanceof Date) {
    if (isNaN(deger.getTime())) return null;
    return isoYap(deger.getDate(), deger.getMonth() + 1, deger.getFullYear());
  }
  if (typeof deger === 'number') return null;

  const s = String(deger).trim();
  if (s === '') return null;
  const tarihPart = s.split(/[ T]/)[0];

  let m = tarihPart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const yil = +m[1], ay = +m[2], gun = +m[3];
    return gecerliTakvimGunu(gun, ay, yil) ? isoYap(gun, ay, yil) : null;
  }

  m = tarihPart.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (m) {
    const gun = +m[1], ay = +m[2], yil = +m[3];
    return gecerliTakvimGunu(gun, ay, yil) ? isoYap(gun, ay, yil) : null;
  }

  return null;
}

// --- Enum normalize (büyük/küçük harf + Türkçe karakter duyarsız) ---

/** Türkçe i ailesi + aksan katlaması: "KİŞİ", "KISI", "kişi" hepsi aynı anahtara iner. */
function enumKatla(s: string): string {
  return s
    .trim()
    .replace(/İ/g, 'i').replace(/I/g, 'i').replace(/ı/g, 'i')
    .toLocaleLowerCase('tr-TR')
    .replace(/ş/g, 's').replace(/ğ/g, 'g').replace(/ü/g, 'u')
    .replace(/ö/g, 'o').replace(/ç/g, 'c');
}

/**
 * Girişi verilen kanonik değerlerden birine eşler (büyük/küçük harf + Türkçe karakter
 * duyarsız). "evet" → "EVET", "KISI" → "KİŞİ". Eşleşmezse null.
 */
export function enumNormalize(deger: any, gecerliDegerler: string[]): string | null {
  if (deger === null || deger === undefined) return null;
  const norm = enumKatla(String(deger));
  if (norm === '') return null;
  for (const g of gecerliDegerler) {
    if (enumKatla(g) === norm) return g;
  }
  return null;
}

/**
 * Cinsiyeti backend'in beklediği kanonik forma indirger: 'E' (erkek) / 'K' (kadın).
 * Backend `cinsiyet == 'E' ? erkek : kadin` mantığıyla çalışır; bu yüzden "ERKEK" gibi tam
 * kelimeler ya da farklı yazımlar (Erkek, bay, M...) yanlışlıkla kadın oluyordu. Burada E/K'ya
 * standardize edilir. Boş → '' , tanınmayan değer → trimlenmiş hali (kullanıcı dropdown'dan düzeltir).
 */
export function cinsiyetNormalize(deger: any): string {
  const s = String(deger === null || deger === undefined ? '' : deger).trim();
  if (s === '') return '';
  const u = s.replace(/İ/g, 'I').replace(/ı/g, 'I').replace(/i/g, 'I').toUpperCase();
  if (u === 'E' || u.indexOf('ERKEK') === 0 || u === 'BAY' || u === 'M' || u === 'MALE') return 'E';
  if (u === 'K' || u.indexOf('KADIN') === 0 || u === 'BAYAN' || u === 'F' || u === 'FEMALE') return 'K';
  return s;
}
